class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.activeElement = null;
    this.isDrawerUpdating = false;
    this.isFlyAnimationRunning = false;
    this.cartState = null;
    this.cartItemsByVariant = new Map();
    this.pendingVariants = new Set();
    this.pendingProductSourceImage = null;
    this.renderTimer = null;

    this.handleDrawerClick = this.handleDrawerClick.bind(this);
    this.handleDrawerChange = this.handleDrawerChange.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);

    this.addEventListener('keyup', (event) => {
      if (event.code === 'Escape') this.close();
    });

    this.addEventListener('click', this.handleDrawerClick, true);
    this.addEventListener('change', this.handleDrawerChange, true);

    this.bindDrawerEvents();
    this.setHeaderCartIconAccessibility();
    this.bindGlobalEvents();
    this.refreshCartState();
  }

  /* =========================================================
     DRAWER
  ========================================================= */

  bindDrawerEvents() {
    const overlay = this.querySelector('#CartDrawer-Overlay');

    if (overlay && overlay.dataset.bofBound !== 'true') {
      overlay.dataset.bofBound = 'true';
      overlay.addEventListener('click', () => this.close());
    }

    this.bindInvoiceToggle();
    this.bindCheckout();
    this.bindDiscount();
    this.bindUpsells();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');

    if (!cartLink || cartLink.dataset.bofDrawerBound === 'true') return;

    cartLink.dataset.bofDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      const key = event.code.toUpperCase();

      if (key === 'SPACE' || key === 'ENTER') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.activeElement = triggeredBy;

    requestAnimationFrame(() => {
      this.classList.add('animate');

      requestAnimationFrame(() => {
        this.classList.add('active');
      });
    });

    document.body.classList.add('overflow-hidden');
    document.documentElement.classList.add('bof-cart-open');

    const panel = this.querySelector('.drawer__inner');
    const closeButton = this.querySelector('.drawer__close');

    window.setTimeout(() => {
      if (typeof trapFocus === 'function' && panel) {
        trapFocus(panel, closeButton || panel);
      }
    }, 340);
  }

  close() {
    this.classList.remove('active');
    document.body.classList.remove('overflow-hidden');
    document.documentElement.classList.remove('bof-cart-open');

    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus(this.activeElement);
    }

    window.setTimeout(() => {
      this.classList.remove('animate');
    }, 340);
  }
  setActiveElement(element) {
    this.activeElement = element;
  }

  /* =========================================================
     SHOPIFY SECTION RENDER
  ========================================================= */

  renderContents(state, options = {}) {
    const {
      openDrawer = true,
      animateIcon = false,
    } = options;

    this.productId = state?.id;

    this.getSectionsToRender().forEach((section) => {
      const currentElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHTML = state?.sections?.[section.section || section.id];

      if (!currentElement || !sectionHTML) return;

      currentElement.innerHTML = this.getSectionInnerHTML(
        sectionHTML,
        section.selector
      );
    });

    if (state?.items) {
      this.setCartState(state);
    }

    window.setTimeout(() => {
      this.bindDrawerEvents();
      this.setHeaderCartIconAccessibility();
      this.renderCardQuantities();

      if (this.pendingProductSourceImage) {
        const image = this.pendingProductSourceImage;

        this.pendingProductSourceImage = null;
        this.animateCartBubble(image);
      } else if (animateIcon) {
        this.animateCartArrival(false);
      }

      if (openDrawer) this.open();
    }, 0);
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const target = parsed.querySelector(selector);

    return target ? target.innerHTML : '';
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  /* =========================================================
     GLOBAL EVENTS
  ========================================================= */

  bindGlobalEvents() {
    if (
      document.documentElement.dataset.bofCartSystemBound ===
      'true'
    ) {
      return;
    }

    document.documentElement.dataset.bofCartSystemBound =
      'true';

    document.addEventListener(
      'click',
      this.handleDocumentClick,
      true
    );

    const observer = new MutationObserver(() => {
      window.clearTimeout(this.renderTimer);

      this.renderTimer = window.setTimeout(() => {
        this.renderCardQuantities();
      }, 100);
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  handleDocumentClick(event) {
    const inlineButton = event.target.closest(
      '.bof-card-quantity__minus, .bof-card-quantity__plus'
    );

    if (inlineButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const control = inlineButton.closest(
        '.bof-card-quantity'
      );

      const variantId = Number(
        control?.dataset.variantId
      );

      const item = this.cartItemsByVariant.get(
        variantId
      );

      if (
        !control ||
        !variantId ||
        !item ||
        this.pendingVariants.has(variantId)
      ) {
        return;
      }

      const current =
        Number(item.quantity) || 0;

      const step = Math.max(
        1,
        Number(control.dataset.step) || 1
      );

      const maximum =
        control.dataset.max === undefined
          ? null
          : Number(control.dataset.max);

      let nextQuantity = current;

      if (
        inlineButton.classList.contains(
          'bof-card-quantity__minus'
        )
      ) {
        nextQuantity =
          current <= step
            ? 0
            : current - step;
      } else {
        nextQuantity = current + step;

        if (
          maximum !== null &&
          nextQuantity > maximum
        ) {
          return;
        }
      }

      this.changeVariantQuantity(
        variantId,
        item.key,
        nextQuantity
      );

      return;
    }

    const cardAddButton = event.target.closest(
      'product-form[data-bof-card-cart="true"] .bof-card-add-button'
    );

    if (cardAddButton) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const productForm = cardAddButton.closest(
        'product-form[data-bof-card-cart="true"]'
      );

      if (productForm) {
        this.addCardProduct(
          productForm,
          cardAddButton
        );
      }

      return;
    }

    /*
      Ürün sayfasının normal Shopify formunu değiştirmiyoruz.
      Yalnızca başarılı eklemeden sonra fly animasyonunda
      kullanılacak görseli geçici olarak saklıyoruz.
    */
    const productPageAddButton = event.target.closest(
      'product-form:not([data-bof-card-cart="true"]) button[name="add"]'
    );

    if (
      productPageAddButton &&
      !productPageAddButton.disabled
    ) {
      const form = productPageAddButton.closest(
        'form[action*="/cart/add"]'
      );

      this.pendingProductSourceImage = form
        ? this.findSourceImageForForm(form)
        : null;

      window.setTimeout(() => {
        this.pendingProductSourceImage = null;
      }, 2500);
    }
  }

  /* =========================================================
     CARD ADD TO CART
  ========================================================= */

  async addCardProduct(productForm, addButton) {
    const form = productForm.querySelector(
      'form[action*="/cart/add"]'
    );

    const variantId =
      this.getCardVariantId(productForm);

    if (
      !form ||
      !variantId ||
      this.pendingVariants.has(variantId)
    ) {
      return;
    }

    const existingItem =
      this.cartItemsByVariant.get(variantId);

    if (existingItem?.quantity > 0) {
      this.renderCardQuantities();
      return;
    }

    const meta =
      this.getCardVariantMeta(productForm);

    if (
      meta.available === false ||
      meta.max === 0 ||
      addButton.disabled ||
      addButton.getAttribute('aria-disabled') ===
        'true'
    ) {
      this.showCardAddButton(
        productForm,
        addButton
      );

      return;
    }

    const sourceImage =
      this.findSourceImageForForm(form);

    this.pendingVariants.add(variantId);
    this.setCardButtonLoading(
      addButton,
      true
    );

    try {
      const formData = new FormData(form);

      formData.set('quantity', '1');

      formData.set(
        'sections',
        'cart-drawer,cart-icon-bubble'
      );

      formData.set(
        'sections_url',
        `${window.Shopify.routes.root}cart`
      );

      const response = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
          body: formData,
        }
      );

      const state = await response.json();

      if (!response.ok) {
        throw new Error(
          state.description ||
            state.message ||
            'Ürün sepete eklenemedi.'
        );
      }

      let cart = null;

      for (let attempt = 0; attempt < 5; attempt += 1) {
        cart = await this.refreshCartState();

        if (
          cart?.items?.some(
            (item) => Number(item.variant_id) === Number(variantId)
          )
        ) {
          break;
        }

        await new Promise((resolve) => setTimeout(resolve, 250));
      }

      const expectedItem = cart?.items?.some(
        (item) => Number(item.variant_id) === Number(variantId)
      );

      if (!expectedItem) {
        // Safety fallback: never leave the customer with a blank cart drawer.
        window.location.assign(`${window.Shopify.routes.root}cart`);
        return;
      }

      // BOFCARDS QUICK ADD DRAWER OPT-IN — 2026-08-22
      await this.refreshCartSections(cart, {
        openDrawer: productForm.dataset.bofOpenDrawer === 'true',
        animateIcon: false,
      });

      if (this.querySelectorAll('#CartDrawer .cart-item').length < cart.items.length) {
        window.location.assign(`${window.Shopify.routes.root}cart`);
        return;
      }

      if (sourceImage) {
        this.animateCartBubble(sourceImage);
      } else {
        this.animateCartArrival(true);
      }
    } catch (error) {
      this.showAddError(
        form,
        error.message
      );

      console.error(
        'BOFCARDS card add error:',
        error
      );
    } finally {
      this.pendingVariants.delete(variantId);

      this.setCardButtonLoading(
        addButton,
        false
      );

      this.renderCardQuantities();
    }
  }
    /* =========================================================
     CART STATE + CARD RENDER
  ========================================================= */

  async refreshCartState() {
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart.js`,
        {
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Sepet okunamadı.');
      }

      const cart = await response.json();

      this.setCartState(cart);
      this.renderCardQuantities();

      return cart;
    } catch (error) {
      console.warn(
        'BOFCARDS cart sync failed:',
        error
      );

      return null;
    }
  }

  async refreshCartSections(cart, options = {}) {
    if (!cart) return null;

    // Shopify can return stale section HTML in the add response.
    // Re-render against the cart route after the cart cookie is committed.
    await new Promise((resolve) => setTimeout(resolve, 150));

    const url = new URL(
      `${window.Shopify.routes.root}cart`,
      window.location.origin
    );
    url.searchParams.set(
      'sections',
      this.getSectionsToRender()
        .map((section) => section.id)
        .join(',')
    );
    url.searchParams.set('_bof', Date.now().toString());

    const fetchSections = async () => {
      const response = await fetch(
        `${url.pathname}${url.search}`,
        {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
          },
        }
      );

      if (!response.ok) {
        throw new Error('Sepet bölümleri yenilenemedi.');
      }

      return response.json();
    };

    let sections = await fetchSections();

    if (
      cart.item_count > 0 &&
      !sections['cart-drawer']?.includes('id="CartDrawer-Item-')
    ) {
      await new Promise((resolve) => setTimeout(resolve, 800));
      url.searchParams.set('_bof', Date.now().toString());
      sections = await fetchSections();
    }

    this.renderContents(
      {
        ...cart,
        sections,
      },
      options
    );

    if (cart.item_count > 0) {
      this.classList.remove('is-empty');

      if (this.querySelectorAll('#CartDrawer .cart-item').length < cart.items.length) {
        const fullCartResponse = await fetch(
          `${window.Shopify.routes.root}cart?_bof=${Date.now()}`,
          {
            cache: 'no-store',
            credentials: 'same-origin',
          }
        );

        if (fullCartResponse.ok) {
          const fullCartHTML = await fullCartResponse.text();
          const parsedDocument = new DOMParser()
            .parseFromString(fullCartHTML, 'text/html');
          const parsedDrawer = parsedDocument.querySelector('#CartDrawer');
          const parsedIcon = parsedDocument.querySelector('#cart-icon-bubble');
          const currentDrawer = this.querySelector('#CartDrawer');
          const currentIcon = document.querySelector('#cart-icon-bubble');

          if (parsedDrawer && currentDrawer) {
            currentDrawer.innerHTML = parsedDrawer.innerHTML;
            this.bindDrawerEvents();
          }

          if (parsedIcon && currentIcon) {
            currentIcon.innerHTML = parsedIcon.innerHTML;
          }
        }
      }
    }

    return cart;
  }

  setCartState(cart) {
    this.cartState = cart;
    this.cartItemsByVariant.clear();

    (cart?.items || []).forEach((item) => {
      const variantId = Number(
        item.variant_id
      );

      const existing =
        this.cartItemsByVariant.get(
          variantId
        );

      if (existing) {
        existing.quantity += Number(
          item.quantity
        );
      } else {
        this.cartItemsByVariant.set(
          variantId,
          {
            key: item.key,
            quantity: Number(
              item.quantity
            ),
          }
        );
      }
    });
  }

  getCardProductForms() {
    return Array.from(
      document.querySelectorAll(
        'product-form[data-bof-card-cart="true"]'
      )
    );
  }

  getCardForm(productForm) {
    return productForm.querySelector(
      'form[action*="/cart/add"]'
    );
  }

  getCardVariantId(productForm) {
    const form =
      this.getCardForm(productForm);

    const input =
      form?.querySelector(
        '[name="id"]'
      );

    return Number(
      input?.value ||
        productForm.dataset.bofVariantId
    );
  }

  getCardVariantMeta(productForm) {
    const maxValue =
      productForm.dataset.bofMax;

    return {
      available:
        this.getCardForm(productForm)
          ?.querySelector('[name="id"]')
          ?.disabled !== true,

      min:
        Math.max(
          1,
          Number(
            productForm.dataset.bofMin
          ) || 1
        ),

      step:
        Math.max(
          1,
          Number(
            productForm.dataset.bofStep
          ) || 1
        ),

      max:
        maxValue === undefined ||
        maxValue === ''
          ? null
          : Math.max(
              0,
              Number(maxValue)
            ),
    };
  }

  getCardAddButton(productForm) {
    return productForm.querySelector(
      '.bof-card-add-button'
    );
  }

  getButtonTextElement(button) {
    return (
      button?.querySelector(
        ':scope > span:not(.sold-out-message):not(.loading__spinner)'
      ) ||
      button?.querySelector('span') ||
      button
    );
  }

  rememberButtonText(button) {
    if (
      !button ||
      button.dataset.bofOriginalText
    ) {
      return;
    }

    const textElement =
      this.getButtonTextElement(button);

    button.dataset.bofOriginalText =
      textElement?.textContent.trim() ||
      'Sepete Ekle';
  }

  setCardButtonLoading(
    button,
    loading
  ) {
    if (!button) return;

    this.rememberButtonText(button);

    const textElement =
      this.getButtonTextElement(button);

    if (loading) {
      button.setAttribute(
        'aria-busy',
        'true'
      );

      button.style.pointerEvents =
        'none';

      button.classList.add(
        'bof-add-loading'
      );

      if (textElement) {
        textElement.textContent =
          'Ekleniyor...';
      }

      return;
    }

    button.removeAttribute(
      'aria-busy'
    );

    button.style.pointerEvents = '';

    button.classList.remove(
      'bof-add-loading'
    );

    const productForm =
      button.closest(
        'product-form[data-bof-card-cart="true"]'
      );

    const variantId =
      productForm
        ? this.getCardVariantId(
            productForm
          )
        : null;

    const item =
      variantId
        ? this.cartItemsByVariant.get(
            variantId
          )
        : null;

    if (
      textElement &&
      !item?.quantity
    ) {
      textElement.textContent =
        button.dataset.bofOriginalText ||
        'Sepete Ekle';
    }
  }

  renderCardQuantities() {
    this.getCardProductForms().forEach(
      (productForm) => {
        const variantId =
          this.getCardVariantId(
            productForm
          );

        const addButton =
          this.getCardAddButton(
            productForm
          );

        if (
          !variantId ||
          !addButton
        ) {
          return;
        }

        const item =
          this.cartItemsByVariant.get(
            variantId
          );

        if (
          item?.quantity > 0
        ) {
          this.showCardQuantity(
            productForm,
            addButton,
            variantId,
            item.quantity
          );
        } else {
          this.showCardAddButton(
            productForm,
            addButton
          );
        }
      }
    );
  }

  showCardQuantity(
    productForm,
    addButton,
    variantId,
    quantity
  ) {
    const meta =
      this.getCardVariantMeta(
        productForm
      );

    this.rememberButtonText(
      addButton
    );

    addButton.removeAttribute(
      'aria-busy'
    );

    addButton.style.pointerEvents = '';

    addButton.classList.remove(
      'bof-add-loading',
      'bof-product-in-cart',
      'bof-card-sold-out'
    );

    if (
      !addButton.dataset
        .bofOriginalDisplay
    ) {
      addButton.dataset.bofOriginalDisplay =
        addButton.style.display || '';
    }

    addButton.hidden = true;
    addButton.style.display = 'none';

    let control =
      productForm.querySelector(
        '.bof-card-quantity'
      );

    if (!control) {
      control =
        document.createElement('div');

      control.className =
        'bof-card-quantity';

      control.innerHTML = `
        <button
          type="button"
          class="bof-card-quantity__button bof-card-quantity__minus"
          aria-label="Adedi azalt"
        >
          −
        </button>

        <span
          class="bof-card-quantity__value"
          aria-live="polite"
        >
          1
        </span>

        <button
          type="button"
          class="bof-card-quantity__button bof-card-quantity__plus"
          aria-label="Adedi artır"
        >
          +
        </button>
      `;

      const container =
        addButton.parentElement;

      container.appendChild(
        control
      );
    }

    control.dataset.variantId =
      String(variantId);

    control.dataset.min =
      String(meta.min);

    control.dataset.step =
      String(meta.step);

    if (meta.max === null) {
      delete control.dataset.max;
    } else {
      control.dataset.max =
        String(meta.max);
    }

    const value =
      control.querySelector(
        '.bof-card-quantity__value'
      );

    const minus =
      control.querySelector(
        '.bof-card-quantity__minus'
      );

    const plus =
      control.querySelector(
        '.bof-card-quantity__plus'
      );

    const pending =
      this.pendingVariants.has(
        variantId
      );

    value.textContent =
      String(quantity);

    control.classList.toggle(
      'is-loading',
      pending
    );

    /*
      Adet 1'de bile eksi aktif.
      Basılırsa ürün tamamen silinir.
    */
    minus.disabled = pending;

    /*
      Stok sınırına gelince yalnızca artı kapanır.
    */
    plus.disabled =
      pending ||
      (
        meta.max !== null &&
        quantity + meta.step >
          meta.max
      );

    plus.setAttribute(
      'aria-label',
      plus.disabled && !pending
        ? 'Maksimum stok adedine ulaşıldı'
        : 'Adedi artır'
    );
  }

  showCardAddButton(
    productForm,
    addButton
  ) {
    const control =
      productForm.querySelector(
        '.bof-card-quantity'
      );

    if (control) {
      control.remove();
    }

    const meta =
      this.getCardVariantMeta(
        productForm
      );

    const textElement =
      this.getButtonTextElement(
        addButton
      );

    const soldOutMessage =
      addButton.querySelector(
        '.sold-out-message'
      );

    const unavailable =
      meta.available === false ||
      meta.max === 0;

    addButton.hidden = false;
    addButton.style.removeProperty(
      'display'
    );

    addButton.classList.remove(
      'bof-add-loading',
      'bof-product-in-cart'
    );

    addButton.removeAttribute(
      'aria-busy'
    );

    if (unavailable) {
      addButton.disabled = true;

      addButton.setAttribute(
        'disabled',
        'disabled'
      );

      addButton.setAttribute(
        'aria-disabled',
        'true'
      );

      addButton.classList.add(
        'bof-card-sold-out'
      );

      addButton.style.pointerEvents =
        'none';

      if (textElement) {
        textElement.textContent =
          'Stokta Yok';
      }

      if (soldOutMessage) {
        soldOutMessage.classList.add(
          'hidden'
        );
      }

      return;
    }

    addButton.disabled = false;

    addButton.removeAttribute(
      'disabled'
    );

    addButton.removeAttribute(
      'aria-disabled'
    );

    addButton.classList.remove(
      'bof-card-sold-out'
    );

    addButton.style.pointerEvents = '';

    if (textElement) {
      textElement.textContent =
        addButton.dataset.bofOriginalText ||
        'Sepete Ekle';
    }

    if (soldOutMessage) {
      soldOutMessage.classList.add(
        'hidden'
      );
    }
  }

  async changeVariantQuantity(
    variantId,
    lineKey,
    quantity
  ) {
    if (
      this.pendingVariants.has(
        variantId
      )
    ) {
      return;
    }

    this.pendingVariants.add(
      variantId
    );

    this.renderCardQuantities();

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/change.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',
          },

          body: JSON.stringify({
            id: lineKey,
            quantity,

            sections:
              'cart-drawer,cart-icon-bubble',

            sections_url:
              window.location.pathname,
          }),
        }
      );

      const state =
        await response.json();

      if (!response.ok) {
        throw new Error(
          state.description ||
            'Sepet güncellenemedi.'
        );
      }

      this.setCartState(state);

      this.renderContents(
        state,
        {
          openDrawer: false,
          animateIcon: false,
        }
      );
    } catch (error) {
      console.error(
        'BOFCARDS card quantity error:',
        error
      );

      await this.refreshCartState();
    } finally {
      this.pendingVariants.delete(
        variantId
      );

      this.renderCardQuantities();
    }
  }

  findSourceImageForForm(form) {
    const productContainer =
      form.closest(
        [
          '.card-wrapper',
          '.product-card-wrapper',
          '.card',
          '.product',
          'product-info',
          '.quick-add-modal',
        ].join(',')
      );

    return (
      productContainer?.querySelector(
        [
          '.card__media img',
          '.media img',
          '.product__media img',
          '.product-media-container img',
          'img',
        ].join(',')
      ) || null
    );
  }

  showAddError(form, message) {
    const productForm =
      form.closest('product-form');

    const errorWrapper =
      productForm?.querySelector(
        '.product-form__error-message-wrapper'
      );

    const errorText =
      productForm?.querySelector(
        '.product-form__error-message'
      );

    if (errorWrapper) {
      errorWrapper.hidden = false;
    }

    if (errorText) {
      errorText.textContent =
        message ||
        'Ürün sepete eklenemedi.';
    }
  }
    /* =========================================================
     FLY TO CART
  ========================================================= */

  animateCartBubble(sourceImage) {
    const cartIcon =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (
      !cartIcon ||
      !sourceImage ||
      this.isFlyAnimationRunning
    ) {
      if (!sourceImage) {
        this.animateCartArrival(true);
      }

      return;
    }

    const imageRect =
      sourceImage.getBoundingClientRect();

    const cartRect =
      cartIcon.getBoundingClientRect();

    if (
      imageRect.width < 5 ||
      imageRect.height < 5 ||
      cartRect.width < 5 ||
      cartRect.height < 5
    ) {
      this.animateCartArrival(true);
      return;
    }

    this.isFlyAnimationRunning = true;

    const flyingImage =
      sourceImage.cloneNode(true);

    flyingImage.removeAttribute('id');
    flyingImage.removeAttribute('loading');
    flyingImage.removeAttribute('srcset');
    flyingImage.removeAttribute('sizes');

    Object.assign(
      flyingImage.style,
      {
        position: 'fixed',
        zIndex: '999999',
        pointerEvents: 'none',

        left: `${imageRect.left}px`,
        top: `${imageRect.top}px`,

        width: `${imageRect.width}px`,
        height: `${imageRect.height}px`,

        margin: '0',
        padding: '5px',

        objectFit: 'contain',

        borderRadius: '16px',

        border:
          '1px solid rgba(255,255,255,0.96)',

        background: '#ffffff',

        boxShadow:
          '0 18px 46px rgba(0,0,0,0.22),' +
          '0 5px 16px rgba(250,86,8,0.16)',

        transformOrigin:
          'center center',

        willChange:
          'transform, opacity',
      }
    );

    document.body.appendChild(
      flyingImage
    );

    const startX =
      imageRect.left +
      imageRect.width / 2;

    const startY =
      imageRect.top +
      imageRect.height / 2;

    const targetX =
      cartRect.left +
      cartRect.width / 2;

    const targetY =
      cartRect.top +
      cartRect.height / 2;

    const translateX =
      targetX - startX;

    const translateY =
      targetY - startY;

    const distance =
      Math.hypot(
        translateX,
        translateY
      );

    const arcHeight =
      Math.min(
        120,
        Math.max(
          48,
          distance * 0.16
        )
      );

    const rotation =
      translateX >= 0
        ? 7
        : -7;

    const animation =
      flyingImage.animate(
        [
          {
            transform:
              'translate3d(0,0,0) ' +
              'scale(1) ' +
              'rotate(0deg)',

            opacity: 1,
          },

          {
            transform:
              'translate3d(0,-9px,0) ' +
              'scale(1.04) ' +
              'rotate(0deg)',

            opacity: 1,
            offset: 0.12,
          },

          {
            transform:
              `translate3d(` +
              `${translateX * 0.34}px,` +
              `${translateY * 0.24 - arcHeight}px,` +
              `0) ` +
              `scale(0.8) ` +
              `rotate(${rotation * 0.35}deg)`,

            opacity: 0.97,
            offset: 0.43,
          },

          {
            transform:
              `translate3d(` +
              `${translateX * 0.74}px,` +
              `${translateY * 0.68 - arcHeight * 0.4}px,` +
              `0) ` +
              `scale(0.4) ` +
              `rotate(${rotation}deg)`,

            opacity: 0.82,
            offset: 0.77,
          },

          {
            transform:
              `translate3d(` +
              `${translateX}px,` +
              `${translateY}px,` +
              `0) ` +
              `scale(0.07) ` +
              `rotate(${rotation * 1.2}deg)`,

            opacity: 0,
          },
        ],
        {
          duration: 780,

          easing:
            'cubic-bezier(0.2,0.76,0.18,1)',

          fill: 'forwards',
        }
      );

    const cleanup = () => {
      flyingImage.remove();

      this.isFlyAnimationRunning =
        false;
    };

    animation.onfinish = () => {
      cleanup();

      this.animateCartArrival(true);
    };

    animation.oncancel =
      cleanup;
  }

  animateCartArrival(
    withRing = false
  ) {
    const cartIcon =
      document.querySelector(
        '#cart-icon-bubble'
      );

    if (!cartIcon) {
      return;
    }

    if (
      typeof cartIcon.animate ===
      'function'
    ) {
      cartIcon.animate(
        [
          {
            transform:
              'scale(1)',
          },

          {
            transform:
              'scale(0.94)',

            offset: 0.25,
          },

          {
            transform:
              'scale(1.18)',

            offset: 0.56,
          },

          {
            transform:
              'scale(1)',
          },
        ],
        {
          duration: 470,

          easing:
            'cubic-bezier(0.22,1,0.36,1)',
        }
      );
    }

    const countBubble =
      cartIcon.querySelector(
        '.cart-count-bubble'
      );

    if (
      countBubble &&
      typeof countBubble.animate ===
        'function'
    ) {
      countBubble.animate(
        [
          {
            transform:
              'scale(1)',
          },

          {
            transform:
              'scale(1.4)',

            backgroundColor:
              '#fa5608',

            color:
              '#ffffff',

            offset: 0.5,
          },

          {
            transform:
              'scale(1)',
          },
        ],
        {
          duration: 520,

          easing:
            'cubic-bezier(0.22,1,0.36,1)',
        }
      );
    }

    if (withRing) {
      this.createCartArrivalRing(
        cartIcon
      );
    }
  }

  createCartArrivalRing(cartIcon) {
    const rect =
      cartIcon.getBoundingClientRect();

    const ring =
      document.createElement('span');

    Object.assign(
      ring.style,
      {
        position: 'fixed',
        zIndex: '999998',
        pointerEvents: 'none',

        left:
          `${rect.left + rect.width / 2}px`,

        top:
          `${rect.top + rect.height / 2}px`,

        width: '12px',
        height: '12px',

        border:
          '2px solid #fa5608',

        borderRadius: '50%',

        transform:
          'translate(-50%,-50%) scale(0.45)',

        opacity: '0.85',
      }
    );

    document.body.appendChild(
      ring
    );

    const animation =
      ring.animate(
        [
          {
            transform:
              'translate(-50%,-50%) scale(0.45)',

            opacity: 0.85,
          },

          {
            transform:
              'translate(-50%,-50%) scale(3.1)',

            opacity: 0,
          },
        ],
        {
          duration: 500,
          easing: 'ease-out',
          fill: 'forwards',
        }
      );

    animation.onfinish = () => {
      ring.remove();
    };
  }

  /* =========================================================
     DRAWER QUANTITY / REMOVE
  ========================================================= */

  handleDrawerClick(event) {
    const removeButton =
      event.target.closest(
        [
          'cart-remove-button button',
          'cart-remove-button a',
        ].join(',')
      );

    if (
      removeButton &&
      this.contains(removeButton)
    ) {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const removeContainer =
        removeButton.closest(
          'cart-remove-button'
        );

      const index =
        Number(
          removeContainer?.dataset.index
        );

      if (index) {
        this.changeLine(
          index,
          0
        );
      }

      return;
    }

    const quantityButton =
      event.target.closest(
        '.cart-item__quantity .quantity__button'
      );

    if (
      !quantityButton ||
      !this.contains(quantityButton)
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const quantityInput =
      quantityButton
        .closest('quantity-input')
        ?.querySelector(
          '.quantity__input'
        );

    if (!quantityInput) {
      return;
    }

    const index =
      Number(
        quantityInput.dataset.index
      );

    const current =
      Number(
        quantityInput.value
      ) || 0;

    const step =
      Number(
        quantityInput.step
      ) || 1;

    const maximum =
      quantityInput.max !== ''
        ? Number(
            quantityInput.max
          )
        : Infinity;

    let next = current;

    if (
      quantityButton.name ===
      'plus'
    ) {
      next =
        Math.min(
          current + step,
          maximum
        );
    }

    if (
      quantityButton.name ===
      'minus'
    ) {
      next =
        current <= step
          ? 0
          : current - step;
    }

    if (
      index &&
      next !== current
    ) {
      quantityInput.value = next;

      this.changeLine(
        index,
        next
      );
    }
  }

  handleDrawerChange(event) {
    const input =
      event.target.closest(
        '.cart-item__quantity .quantity__input'
      );

    if (
      !input ||
      !this.contains(input)
    ) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    const index =
      Number(input.dataset.index);

    const maximum =
      input.max !== ''
        ? Number(input.max)
        : Infinity;

    let quantity =
      Number(input.value) || 0;

    quantity =
      Math.min(
        quantity,
        maximum
      );

    if (quantity < 1) {
      quantity = 0;
    }

    input.value = quantity;

    if (index) {
      this.changeLine(
        index,
        quantity
      );
    }
  }

  async changeLine(
    index,
    quantity
  ) {
    if (this.isDrawerUpdating) {
      return;
    }

    this.isDrawerUpdating = true;

    this.classList.add(
      'is-updating'
    );

    try {
      const response = await fetch(
        `${window.Shopify.routes.root}cart/change.js`,
        {
          method: 'POST',

          headers: {
            'Content-Type':
              'application/json',

            Accept:
              'application/json',
          },

          body: JSON.stringify({
            line: index,
            quantity,

            sections:
              'cart-drawer,cart-icon-bubble',

            sections_url:
              window.location.pathname,
          }),
        }
      );

      const state =
        await response.json();

      if (!response.ok) {
        throw new Error(
          state.description ||
            'Sepet güncellenemedi.'
        );
      }

      this.setCartState(state);

      this.renderContents(
        state,
        {
          openDrawer: false,
          animateIcon: false,
        }
      );

      this.classList.toggle(
        'is-empty',
        state.item_count === 0
      );
    } catch (error) {
      const errorContainer =
        this.querySelector(
          '#CartDrawer-CartErrors'
        );

      if (errorContainer) {
        errorContainer.textContent =
          'Sepet güncellenemedi. Lütfen tekrar dene.';
      }

      console.error(
        'BOFCARDS cart update error:',
        error
      );
    } finally {
      this.isDrawerUpdating = false;

      this.classList.remove(
        'is-updating'
      );
    }
  }
    /* =========================================================
     FATURA
  ========================================================= */

  bindInvoiceToggle() {
    const radios = this.querySelectorAll(
      'input[name="bof_invoice_type_ui"]'
    );

    if (!radios.length) return;

    const updateVisibility = () => {
      const selected =
        this.querySelector(
          'input[name="bof_invoice_type_ui"]:checked'
        )?.value || 'Bireysel';

      const tcField = this.querySelector(
        '.bof-field--tc'
      );

      const taxFields = this.querySelectorAll(
        '.bof-field--tax'
      );

      if (tcField) {
        tcField.hidden =
          selected === 'Kurumsal';
      }

      taxFields.forEach((field) => {
        field.hidden =
          selected !== 'Kurumsal';
      });
    };

    radios.forEach((radio) => {
      if (
        radio.dataset.bofBound ===
        'true'
      ) {
        return;
      }

      radio.dataset.bofBound =
        'true';

      radio.addEventListener(
        'change',
        updateVisibility
      );
    });

    updateVisibility();
  }

  validateInvoice() {
    const type =
      this.querySelector(
        'input[name="bof_invoice_type_ui"]:checked'
      )?.value || 'Bireysel';

    const tc = (
      this.querySelector(
        '#BofTcNo'
      )?.value || ''
    ).replace(/\D/g, '');

    const taxNo = (
      this.querySelector(
        '#BofTaxNo'
      )?.value || ''
    ).replace(/\D/g, '');

    const taxOffice = (
      this.querySelector(
        '#BofTaxOffice'
      )?.value || ''
    ).trim();

    const error = this.querySelector(
      '#BofInvoiceError'
    );

    let message = '';

    if (type === 'Bireysel') {
      if (tc.length !== 11) {
        message =
          'Lütfen 11 haneli T.C. Kimlik Numaranı gir.';
      }
    } else if (taxNo.length !== 10) {
      message =
        'Lütfen 10 haneli vergi numaranı gir.';
    } else if (!taxOffice) {
      message =
        'Lütfen vergi dairesini gir.';
    }

    if (error) {
      error.textContent = message;
      error.hidden = !message;
    }

    if (message) {
      const details =
        this.querySelector(
          '.bof-invoice-details'
        );

      if (details) {
        details.open = true;
      }

      return null;
    }

    return {
      type,

      tc:
        type === 'Bireysel'
          ? tc
          : '',

      taxNo:
        type === 'Kurumsal'
          ? taxNo
          : '',

      taxOffice:
        type === 'Kurumsal'
          ? taxOffice
          : '',
    };
  }

  bindCheckout() {
    const button = this.querySelector(
      '#BofCartCheckout'
    );

    if (
      !button ||
      button.dataset.bofBound ===
        'true'
    ) {
      return;
    }

    button.dataset.bofBound =
      'true';

    button.addEventListener(
      'click',
      async () => {
        const invoice =
          this.validateInvoice();

        if (!invoice) return;

        button.disabled = true;
        button.classList.add(
          'is-loading'
        );
        button.textContent =
          'Hazırlanıyor...';

        try {
          const response = await fetch(
            `${window.Shopify.routes.root}cart/update.js`,
            {
              method: 'POST',

              headers: {
                'Content-Type':
                  'application/json',

                Accept:
                  'application/json',
              },

              body: JSON.stringify({
                attributes: {
                  'Fatura Türü':
                    invoice.type,

                  'T.C. Kimlik No':
                    invoice.tc,

                  'Vergi No':
                    invoice.taxNo,

                  'Vergi Dairesi':
                    invoice.taxOffice,
                },
              }),
            }
          );

          if (!response.ok) {
            throw new Error(
              'Fatura bilgileri kaydedilemedi.'
            );
          }

          window.location.href =
            '/checkout';
        } catch (error) {
          const message =
            this.querySelector(
              '#BofInvoiceError'
            );

          if (message) {
            message.textContent =
              'Bilgiler kaydedilemedi. Lütfen tekrar dene.';

            message.hidden = false;
          }

          button.disabled = false;

          button.classList.remove(
            'is-loading'
          );

          button.textContent =
            'Ödemeye Geç';
        }
      }
    );
  }

  /* =========================================================
     DISCOUNT
  ========================================================= */

  bindDiscount() {
    const button = this.querySelector(
      '#BofApplyDiscount'
    );

    const input = this.querySelector(
      '#BofCartDiscountCode'
    );

    const message = this.querySelector(
      '#BofDiscountMessage'
    );

    if (
      !button ||
      !input ||
      button.dataset.bofBound ===
        'true'
    ) {
      return;
    }

    button.dataset.bofBound =
      'true';

    const apply = () => {
      const code = input.value.trim();

      if (!code) {
        if (message) {
          message.textContent =
            'Lütfen bir indirim kodu gir.';
        }

        input.focus();
        return;
      }

      button.disabled = true;
      button.textContent =
        'Uygulanıyor...';

      const returnPath =
        `${window.location.pathname}` +
        `${window.location.search}`;

      window.location.href =
        `/discount/${encodeURIComponent(code)}` +
        `?redirect=${encodeURIComponent(returnPath)}`;
    };

    button.addEventListener(
      'click',
      apply
    );

    input.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          apply();
        }
      }
    );
  }

  /* =========================================================
     UPSELL
  ========================================================= */

  bindUpsells() {
    this.querySelectorAll(
      '.bof-upsell-add'
    ).forEach((button) => {
      if (
        button.dataset.bofBound ===
        'true'
      ) {
        return;
      }

      button.dataset.bofBound =
        'true';

      button.addEventListener(
        'click',
        async () => {
          const variantId = Number(
            button.dataset.variantId
          );

          if (
            !variantId ||
            this.pendingVariants.has(
              variantId
            )
          ) {
            return;
          }

          this.pendingVariants.add(
            variantId
          );

          const originalText =
            button.textContent;

          button.disabled = true;
          button.textContent = '...';

          try {
            const response = await fetch(
              `${window.Shopify.routes.root}cart/add.js`,
              {
                method: 'POST',

                headers: {
                  'Content-Type':
                    'application/json',

                  Accept:
                    'application/json',
                },

                body: JSON.stringify({
                  items: [
                    {
                      id: variantId,
                      quantity: 1,
                    },
                  ],

                  sections:
                    'cart-drawer,cart-icon-bubble',

                  sections_url:
                    window.location.pathname,
                }),
              }
            );

            const state =
              await response.json();

            if (!response.ok) {
              throw new Error(
                state.description ||
                  'Ürün eklenemedi.'
              );
            }

            this.renderContents(
              state,
              {
                openDrawer: false,
                animateIcon: true,
              }
            );

            await this.refreshCartState();
          } catch (error) {
            button.disabled = false;

            button.textContent =
              'Tekrar dene';

            console.error(
              'BOFCARDS upsell error:',
              error
            );

            return;
          } finally {
            this.pendingVariants.delete(
              variantId
            );
          }

          if (
            document.body.contains(button)
          ) {
            button.disabled = false;

            button.textContent =
              originalText;
          }
        }
      );
    });
  }
}

if (
  !customElements.get(
    'cart-drawer'
  )
) {
  customElements.define(
    'cart-drawer',
    CartDrawer
  );
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },

      {
        id: 'cart-icon-bubble',
        section:
          'cart-icon-bubble',
        selector:
          '.shopify-section',
      },
    ];
  }
}

if (
  !customElements.get(
    'cart-drawer-items'
  )
) {
  customElements.define(
    'cart-drawer-items',
    CartDrawerItems
  );
}